import { For, Show, type Component } from 'solid-js';
import type { GapProfile, PortReport } from '~/domain/port';
import type { PlacedBet, WheelStyle } from '~/domain/types';
import { betLabel } from '~/domain/bets';
import { getWheel } from '~/domain/wheels';
import WheelThumbnail from '~/components/WheelThumbnail';

/**
 * The port report (SPEC.md §5).
 *
 * Porting is explicit and never automatic, so this screen has to earn the
 * click. The bets that fall away matter less than the thing the user actually
 * came for: the coverage they designed around the ring is not the coverage they
 * will get, because the two wheels share no pocket sequence at all.
 */

export interface PortDialogProps {
  report: PortReport;
  sourceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const styleLabel = (style: PortReport['from']) => getWheel(style).label.toLowerCase();

const PortDialog: Component<PortDialogProps> = (props) => (
  <div class="flex h-full flex-col gap-4 overflow-y-auto p-4">
    <header>
      <h2 class="text-[13px] font-medium text-ink">
        Port “{props.sourceName}” to the {styleLabel(props.report.to)} wheel
      </h2>
      <p class="mt-0.5 text-[11px] text-ink-faint">
        This creates a new spread. The original is left exactly as it is.
      </p>
    </header>

    {/* The headline, and the reason porting is a deliberate act. */}
    <Show when={props.report.neighboursBroken}>
      <section class="rounded-lg border border-remove/40 bg-remove/10 p-3">
        <h3 class="text-[11px] font-medium text-remove">
          Physical neighbours do not carry over
        </h3>
        <p class="mt-1 text-[11px] leading-relaxed text-ink-dim">
          The two wheels have entirely different pocket sequences. Bets that sit side by
          side on the {styleLabel(props.report.from)} ring will be scattered around the{' '}
          {styleLabel(props.report.to)} one. The chips move; the coverage you designed does
          not.
        </p>
      </section>
    </Show>

    {/* Before and after, side by side — the redistribution made visible. */}
    <section class="rounded-lg border border-line bg-surface-1 p-3">
      <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Ring coverage
      </h3>
      <div class="grid grid-cols-2 gap-3">
        <GapColumn
          heading={`Now — ${styleLabel(props.report.from)}`}
          profile={props.report.beforeGaps}
          bets={props.report.original}
          style={props.report.from}
        />
        <GapColumn
          heading={`After — ${styleLabel(props.report.to)}`}
          profile={props.report.afterGaps}
          bets={props.report.carried}
          style={props.report.to}
        />
      </div>
      <p class="mt-2 border-t border-line pt-2 text-[10px] leading-relaxed text-ink-faint">
        Longest bare run is the number that matters: it is the stretch of wheel where
        nothing you have staked can win.
      </p>
    </section>

    <section class="rounded-lg border border-line bg-surface-1 p-3">
      <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Bets
      </h3>
      <dl class="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div>
          <dt class="text-ink-faint">Carried</dt>
          <dd class="tnum text-gain">
            {props.report.carried.length} bets · {props.report.chipsCarried} chips
          </dd>
        </div>
        <div>
          <dt class="text-ink-faint">Dropped</dt>
          <dd
            class="tnum"
            classList={{
              'text-loss': props.report.dropped.length > 0,
              'text-ink-dim': props.report.dropped.length === 0,
            }}
          >
            {props.report.dropped.length} bets · {props.report.chipsDropped} chips
          </dd>
        </div>
      </dl>

      <Show when={props.report.dropped.length > 0}>
        <ul class="mt-2 space-y-1 border-t border-line pt-2">
          <For each={props.report.dropped}>
            {(entry) => (
              <li class="text-[11px]">
                <span class="text-ink">
                  {betLabel(entry.bet)}
                  <span class="tnum ml-1 text-ink-faint">×{entry.bet.chips}</span>
                </span>
                <span class="block text-[10px] text-ink-faint">{entry.reason}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>

    <Show when={props.report.sectorsLost.length > 0}>
      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Call bets lost
        </h3>
        <p class="mt-1 text-[11px] text-ink-dim">
          {props.report.sectorsLost.join(', ')} — the French call bets are defined as arcs of
          the European ring. There is no equivalent on a double-zero wheel, so even where the
          individual chips survive, the sector does not.
        </p>
      </section>
    </Show>

    <div class="mt-auto flex gap-2 pt-2">
      <button
        type="button"
        onClick={props.onCancel}
        class="flex-1 rounded border border-line bg-surface-2 px-3 py-1.5 text-[11px] text-ink-dim hover:border-line-strong hover:text-ink"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={props.onConfirm}
        class="flex-1 rounded border border-placed-strong bg-placed/25 px-3 py-1.5 text-[11px] font-medium text-ink hover:bg-placed/40"
      >
        Create ported spread
      </button>
    </div>
  </div>
);

const GapColumn: Component<{
  heading: string;
  profile: GapProfile;
  bets: readonly PlacedBet[];
  style: WheelStyle;
}> = (props) => (
  <div class="rounded border border-line bg-surface-2 p-2">
    <div class="mb-1.5 flex items-center gap-2">
      <WheelThumbnail bets={props.bets} wheelStyle={props.style} size={38} />
      <span class="text-[10px] leading-tight text-ink-dim">{props.heading}</span>
    </div>
    <dl class="space-y-0.5 text-[11px]">
      <Row label="Covered">
        {props.profile.pocketsCovered}/{props.profile.pocketCount}
      </Row>
      <Row label="Bare runs">{props.profile.count}</Row>
      <Row label="Longest bare run">{props.profile.longest}</Row>
    </dl>
  </div>
);

const Row: Component<{ label: string; children: number | string | (number | string)[] }> = (
  props,
) => (
  <div class="flex items-baseline justify-between gap-2">
    <dt class="text-ink-faint">{props.label}</dt>
    <dd class="tnum text-ink">{props.children}</dd>
  </div>
);

export default PortDialog;
